import { Zoning, ZoningType } from '@popety-io/types'

const DEFAULT_UNKNOWN_ZONE = 'Unknown zone'

/**
 * Gets the plan type title for display in the header
 * - General: lupa_name
 * - Cantonal: lupc_name
 * - Partial: lupao_name
 * - Local: lupa_name (if valid) else lup_name
 */
export const getZonePlanTypeTitle = (
  zone: Zoning,
  type: ZoningType,
  unknownZoneLabel?: string
): string => {
  const fallback = unknownZoneLabel || DEFAULT_UNKNOWN_ZONE
  switch (type) {
    case 'general':
      return zone.lupa_name || fallback

    case 'cantonal':
      return zone.lupc_name || fallback

    case 'partial':
      return zone.lupao_name || fallback

    case 'local':
      // Use lupa_name if valid, otherwise lup_name
      if (zone.lupao_name && zone.lupao_name !== 'default_name') {
        return zone.lupao_name
      }
      return zone.lupo_name || fallback

    default:
      return fallback
  }
}

/**
 * Gets the zone name for display in the header (subtitle)
 * - General: lup_name
 * - Cantonal: lupc_plan_type
 * - Partial: lupo_name
 * - Local: lup_plan_type (if lupa_name valid) else "lup_plan_type - lup_name"
 */
export const getZoneNameForHeader = (
  zone: Zoning,
  type: ZoningType,
  unknownZoneLabel?: string
): string => {
  const fallback = unknownZoneLabel || DEFAULT_UNKNOWN_ZONE
  switch (type) {
    case 'general':
      return zone.lup_name || fallback

    case 'cantonal':
      return zone.lupc_plan_type || fallback

    case 'partial':
      return zone.lupo_name || fallback

    case 'local':
      // If lupa_name is valid, just show lup_plan_type
      if (zone.lupao_name && zone.lupao_name == 'default_name') {
        return zone.lupo_plan_type || fallback
      }
      // Otherwise, show "lup_plan_type - lup_name"
      const planType = zone.lupo_plan_type || ''
      const planName = zone.lupo_name || ''
      if (planType && planName) {
        return `${planType} - ${planName}`
      }
      return planType || planName || fallback

    default:
      return fallback
  }
}

interface StatusTranslations {
  current?: string
  future?: string
  past?: string
}

const DEFAULT_STATUS_TRANSLATIONS: StatusTranslations = {
  current: 'Current',
  future: 'Future',
  past: 'Past',
}

/**
 * Gets the status label and color for a zone
 * Uses status_name if available, otherwise falls back to status
 */
export const getZoneStatus = (
  zone: Zoning,
  statusTranslations?: StatusTranslations
): { label: string; variant: 'current' | 'future' | 'past' } | null => {
  if (!zone.status) return null

  const t = { ...DEFAULT_STATUS_TRANSLATIONS, ...statusTranslations }
  const status = zone.status.toLowerCase()

  // Determine variant based on status enum
  let variant: 'current' | 'future' | 'past'
  if (
    status === 'current' ||
    status === 'en_vigueur' ||
    status === 'in_force'
  ) {
    variant = 'current'
  } else if (
    status === 'future' ||
    status === 'futur' ||
    status === 'planned'
  ) {
    variant = 'future'
  } else if (
    status === 'past' ||
    status === 'passé' ||
    status === 'abrogated'
  ) {
    variant = 'past'
  } else {
    return null
  }

  // Always use translated label based on variant
  const label =
    variant === 'current'
      ? t.current || 'Current'
      : variant === 'future'
        ? t.future || 'Future'
        : t.past || 'Past'

  return { label, variant }
}

/**
 * Formats a date string (YYYY-MM-DD) to Swiss format (DD.MM.YYYY)
 */
export const formatSwissDate = (dateString?: string): string | null => {
  if (!dateString) return null

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  } catch {
    return null
  }
}

/**
 * Gets the zone ID based on zone type
 * - General: lupa_id
 * - Cantonal: lupac_id
 * - Partial: lupao_id
 * - Local: lupao_id
 */
export const getZoneId = (zone: Zoning, type: ZoningType): string | null => {
  switch (type) {
    case 'general':
      return zone.lupa_id || zone._id || null
    case 'cantonal':
      return zone.lupac_id || zone._id || null
    case 'partial':
    case 'local':
      return zone.lupao_id || zone._id || null
    default:
      return zone._id || null
  }
}
